<template>
  <UDashboardPanel id="clients" :ui="{ body: 'h-full w-full sm:p-0 p-0' }">
    <!--
    <template #header>
      <UDashboardNavbar title="Clients">
        <template #leading>
          <UDashboardSidebarCollapse />
        </template>
        <template #right>
          <LanguageSwitcher />
        </template>
      </UDashboardNavbar>
    </template>
    -->
    
    <template #body>
      <div class="w-full h-full flex flex-col">
        <div class="px-4 pt-4 pb-4">
          <div class="flex flex-wrap items-center justify-between gap-2">
            <UInput
              v-model="globalSearch"
              class="w-full sm:w-80"
              icon="i-lucide-search"
              :placeholder="t('clients.table.search.global')"
              :ui="{ trailing: 'pe-1' }"
            >
              <template v-if="globalSearch?.length" #trailing>
                <UButton
                  color="neutral"
                  variant="link"
                  size="sm"
                  icon="i-lucide-circle-x"
                  :aria-label="t('workflows.table.filters.clear')"
                  @click="globalSearch = ''"
                />
              </template>
            </UInput>
            
            <!--
            <div class="flex items-center gap-2">
              <AddModal />
            </div>
            -->
          </div>
        </div>

        <div class="flex-1 flex flex-col overflow-hidden">
          <div class="flex-1 min-h-0 pb-[115px] lg:pb-0">
            <UTable
              :columns="columns"
              :data="tableData"
              sticky
              @hover="() => {}"
              class="w-full h-full px-4"
              :ui="{                
                base: 'table-fixed border-separate border-spacing-0',
                thead: '[&>tr]:bg-(--ui-bg-elevated)/50 [&>tr]:after:content-none',
                tbody: '[&>tr]:last:[&>td]:border-b-0',
                th: 'py-2 first:rounded-l-lg last:rounded-r-lg border-y border-(--ui-border) first:border-l last:border-r',
                td: 'border-b border-(--ui-border)'
              }"
            />
          </div>

          <div class="fixed bottom-0 left-0 right-0 lg:sticky bg-(--ui-bg) border-t border-(--ui-border) px-4 py-3.5 z-10">
            <div class="flex items-center justify-between gap-3 min-h-8">
              <div class="text-sm text-(--ui-text-muted)">
                <span v-if="pagination">
                  <span class="hidden lg:inline">{{ t('clients.table.showing') }}</span> {{ ((pagination.currentPage - 1) * pagination.pageSize) + 1 }} - {{ Math.min(pagination.currentPage * pagination.pageSize, pagination.totalItems) }} <span class="hidden lg:inline">{{ t('clients.table.of') }}</span><span class="lg:hidden">/</span> {{ pagination.totalItems }}
                </span>
                <span v-else>{{ t('clients.table.loading') }}</span>
              </div>

              <div class="flex items-center gap-3">
                <USelect
                  v-model="selectedPageSize"
                  :items="pageSizeOptions"
                  size="sm"
                  class="w-20 cursor-pointer"
                  @update:model-value="handlePageSizeChange"
                />
                <UPagination
                  v-if="pagination && pagination.totalItems > pagination.pageSize"
                  v-model:page="currentPaginationPage"
                  :total="pagination.totalItems"
                  :items-per-page="pagination.pageSize"
                  :sibling-count="1"
                  size="sm"
                  class="lg:hidden"
                  :ui="{
                    item: 'cursor-pointer',
                    prev: 'cursor-pointer',
                    next: 'cursor-pointer',
                    first: 'hidden',
                    last: 'hidden'
                  }"
                />
                <UPagination
                  v-if="pagination && pagination.totalItems > pagination.pageSize"
                  v-model:page="currentPaginationPage"
                  :total="pagination.totalItems"
                  :items-per-page="pagination.pageSize"
                  :sibling-count="2"
                  size="sm"
                  class="hidden lg:flex"
                  :ui="{
                    item: 'cursor-pointer',
                    prev: 'cursor-pointer',
                    next: 'cursor-pointer',
                    first: 'cursor-pointer',
                    last: 'cursor-pointer'
                  }"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </UDashboardPanel>
  
  <ClientDetailModal
    v-if="isClientModalOpen && selectedClientId"
    v-model="isClientModalOpen"
    :client-id="selectedClientId"
    :client-name="selectedClientName || undefined"
  />
</template>

<script setup lang="ts">
import { computed, h, onMounted, ref, resolveComponent, watch } from 'vue'
import { debounce } from '@/utils/utils.ts'
import { useBusinessPartnersData, type TransformedBusinessPartner, type BusinessPartnerFilters } from '@/composables/useBusinessPartnersData.ts'
import { useI18n } from 'vue-i18n'
import { usePreferences } from '@/store'

const UInput = resolveComponent('UInput')
const UButton = resolveComponent('UButton')

const { t } = useI18n()
const preferences = usePreferences()

const { data, status, pagination, fetchBusinessPartners, applyFilters, goToPage, changePageSize } = useBusinessPartnersData()

const globalSearch = ref('')
const selectedPageSize = ref(preferences.clientsPageSize)
const currentPaginationPage = ref(1)
const isClientModalOpen = ref(false)
const selectedClientId = ref<string | null>(null)
const selectedClientName = ref<string | null>(null)

const pageSizeOptions = [
  { label: '20', value: 20 },
  { label: '40', value: 40 },
  { label: '50', value: 50 },
  { label: '100', value: 100 }
]

const skeletonData = computed<Array<TransformedBusinessPartner & { _skeleton: boolean }>>(() => {
  if (status.value !== 'pending') return []

  return Array.from({ length: 10 }, (_, index) => ({
    id: `skeleton-${index}`,
    displayName: '',
    partnerType: '',
    addressLine: '',
    sources: [],
    _skeleton: true
  }))
})

const tableData = computed(() => (status.value === 'pending' ? skeletonData.value : data.value ?? []))

const columns = computed(() => [
  {
    id: 'actions',
    cell: ({ row }) => {
      if (row.original._skeleton) {
        return h('div', { class: 'animate-pulse bg-(--ui-bg-accented) h-7 w-7 rounded-full' })
      }
      
      return h(UButton, {
        color: 'primary',
        variant: 'solid',
        icon: 'i-heroicons-magnifying-glass',
        size: 'sm',
        class: 'hover:scale-105 cursor-pointer text-white transition-all duration-200',
        onClick: () => openClientDetail(row.original)
      })
    }
  },
  {
    id: 'id',
    header: t('clients.table.columns.id'),
    cell: ({ row }) => {
      if (row.original._skeleton) {
        return h('div', { class: 'animate-pulse bg-(--ui-bg-accented) h-4 rounded w-24' })
      }

      return h('span', { class: 'text-sm font-medium text-(--ui-text)' }, row.original.id)
    }
  },
  {
    id: 'name',
    header: t('clients.table.columns.name'),
    cell: ({ row }) => {
      if (row.original._skeleton) {
        return h('div', { class: 'animate-pulse bg-(--ui-bg-accented) h-4 rounded w-40' })
      }

      return h('span', { class: 'text-sm font-semibold text-(--ui-text)' }, row.original.displayName)
    }
  }
])

const debouncedFilters = debounce(async (filters: BusinessPartnerFilters) => {
  await applyFilters(filters)
}, 400)

const openClientDetail = (client: TransformedBusinessPartner & { id?: string }) => {
  if (!client?.id) return

  selectedClientId.value = client.id
  selectedClientName.value = client.displayName
  isClientModalOpen.value = true
}

watch(isClientModalOpen, open => {
  if (!open) {
    selectedClientId.value = null
  }
})

watch(globalSearch, newValue => {
  const apiFilters: BusinessPartnerFilters = {}
  const normalized = newValue?.trim()
  
  if (normalized) {
    apiFilters.search = normalized
  }
  
  debouncedFilters(apiFilters)
})

watch(pagination, newPagination => {
  if (!newPagination) return
  
  if (typeof newPagination.pageSize === 'number') {
    selectedPageSize.value = newPagination.pageSize
  }
  
  if (typeof newPagination.currentPage === 'number') {
    currentPaginationPage.value = newPagination.currentPage
  }
}, { immediate: true })

const handlePageChange = async (page: number) => {
  if (!pagination.value) return
  if (page < 1 || page > pagination.value.totalPages) return
  
  await goToPage(page)
}

const handlePageSizeChange = async (newSize: number) => {
  await changePageSize(newSize)
  
  selectedPageSize.value = newSize
  currentPaginationPage.value = 1
  preferences.setClientsPageSize(newSize)
}

watch(currentPaginationPage, async (newPage, oldPage) => {
  if (newPage === oldPage) return
  
  if (newPage !== pagination.value?.currentPage) {
    await handlePageChange(newPage)
  }
})

onMounted(async () => {
  await fetchBusinessPartners(selectedPageSize.value, currentPaginationPage.value)
})
</script>